import { FieldError } from "@imi/ui/components/FieldErrors/FieldErrors.js";
import {
  TextField,
  TextFieldProps,
} from "@imi/ui/components/TextField/TextField.js";
import { useFieldContext } from "@imi/ui/hooks/useAppForm.js";
import { useTimeoutRef } from "@imi/ui/hooks/useTimeoutRef.js";
import { NumberFieldDefinition } from "@src/core/engine/helpers/fieldDefinitions/getNumberDefinition";
import { processNumericValue } from "@src/core/engine/helpers/processNumericValue";
import { useUnitConversion } from "@src/shared/hooks/useUnitConversion";
import { isNullish, isNumber } from "radashi";
import { useCallback, useEffect, useState } from "react";
import { useConvertedFieldValue } from "./helpers/useConvertedFieldValue";
import { useNumberFieldEvents } from "./helpers/useNumberFieldEvents";

type NumberFieldProps = {
  definition: NumberFieldDefinition;
  errors?: FieldError;
  onChange?: (value: number) => void;
  onChangeDebounce?: number;
} & Omit<TextFieldProps<"input">, "onChange">;
export const numberRegex = /^[+-]?(?:\d+|\d*\.\d*)?$/;

const stringifyValue = (value: number | null) => {
  if (isNullish(value)) return "";
  return `${value}`;
};

export function NumberField({
  definition,
  errors,
  onChangeDebounce,
  ...props
}: NumberFieldProps) {
  const timeoutRef = useTimeoutRef();
  const field = useFieldContext<number | null>();
  const { convertToInternationalUnit, getUnitSymbol } = useUnitConversion();
  const { convertedMax, convertedMin, convertFieldValue } =
    useConvertedFieldValue(definition);
  const [isFocused, setIsFocused] = useState(false);

  const [displayValue, setDisplayValue] = useState(
    convertFieldValue(stringifyValue(field.state.value)),
  );

  const onValueChange = useCallback(
    (convertedValue: number | null) => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      const normalized = processNumericValue(convertedValue, definition, {
        conversionFn: convertToInternationalUnit,
        range: { max: definition.max, min: definition.min },
      });

      function changeHandler() {
        field.handleChange(normalized ?? null);
        if (normalized) {
          props?.onChange?.(normalized);
        }
      }
      if (onChangeDebounce) {
        timeoutRef.current = setTimeout(changeHandler, onChangeDebounce);
      } else {
        changeHandler();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [definition, convertToInternationalUnit, onChangeDebounce],
  );

  useEffect(() => {
    if (isFocused) return;
    // setState in effect is intentional: displayValue is a controlled input buffer
    // that must not be reset while the user is focused/typing.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setDisplayValue(convertFieldValue(stringifyValue(field.state.value)));
  }, [field.state.value, isFocused, convertFieldValue]);

  function parseInputValue(value: string) {
    const raw = value.replaceAll(",", "");
    if (!numberRegex.test(raw)) return "regex-fail" as const;

    const parsed = parseFloat(raw);
    if (!isNumber(parsed)) return "parse-fail" as const;

    return parsed;
  }

  const incrementValueBy = useCallback(
    (step: number) => {
      if (!isFocused) return;
      const parsed = parseInputValue(displayValue);
      if (parsed === "regex-fail" || parsed === "parse-fail") return;
      const increment = Math.min(
        Math.max(parsed + step, convertedMin),
        convertedMax,
      );
      setDisplayValue(increment.toString());
      onValueChange(increment);
    },
    [convertedMin, convertedMax, displayValue, isFocused, onValueChange],
  );
  const ref = useNumberFieldEvents({ incrementValueBy, isFocused });
  const parsedInput = parseInputValue(displayValue);

  return (
    <>
      <TextField
        {...props}
        aria-valuemax={convertedMax}
        aria-valuemin={convertedMin}
        aria-valuenow={isNumber(parsedInput) ? parsedInput : undefined}
        data-value={stringifyValue(field.state.value)}
        errors={
          errors ?? field.state.meta.errors.map((error) => error?.message)
        }
        form={field.form.formId}
        inputMode="decimal"
        onBlur={() => {
          setIsFocused(false);
          field.handleBlur();
        }}
        onChange={(e) => {
          if (["", "+", "-"].includes(e.target.value)) {
            setDisplayValue(e.target.value);
            onValueChange(null);
            return;
          }
          const parsed = parseInputValue(e.target.value);
          if (parsed === "regex-fail") return;
          setDisplayValue(e.target.value);
          if (parsed === "parse-fail") return;
          onValueChange(parsed);
        }}
        onFocus={() => {
          setIsFocused(true);
        }}
        ref={ref}
        role="spinbutton"
        suffix={props.suffix ?? getUnitSymbol(definition.physicalType)}
        type="text"
        value={displayValue}
      />
    </>
  );
}
